import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-obsidian grain relative flex items-center justify-center px-4">
      <div className="w-full max-w-md relative">
        <Link to="/" data-testid="admin-login-back-link" className="mb-8 inline-flex items-center gap-2 text-sm text-ivory/50 hover:text-ivory transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
        <div className="bg-ivory p-8 sm:p-10 border border-white/10">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center bg-brand">
              <Lock className="h-5 w-5 text-ivory" />
            </span>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-foreground">Staff Portal</h1>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">SBS Properties · Est. 1952</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Email</label>
              <Input
                data-testid="admin-login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@sbsproperties.co.ke"
                className="mt-2 rounded-none"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Password</label>
              <Input
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 rounded-none"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-4 py-3">{error}</p>
            )}
            <button
              data-testid="admin-login-submit-button"
              disabled={loading}
              className="w-full bg-brand py-3.5 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <p className="mt-6 text-xs text-muted-foreground">Role: Property Manager · Listing Editor</p>
        </div>
      </div>
    </div>
  );
}
